import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IOrganization, OrganizationTypes } from '@app/core/services/organization/organization.interface';
import { SecurityService } from '@app/services/security.service';
import { ConfigFacadeService } from '@core/services/config/config-facade.service';
import { IOUList } from '@modules/admin-panel/domain/models/admin';
import { monthlyCostConfig } from '@modules/commitment-manager/modules/compute-explorer/helpers/filter-ec2-resources';
import { AppRouteHelper } from '@shared/helpers/app-route.helper';
import { getActiveOrganization, handleError } from '@utils/helpers';
import { makeUri } from '@utils/string.tool';
import { BehaviorSubject, Observable, of } from 'rxjs';
import {
	catchError,
	concatMap,
	delay,
	distinctUntilChanged,
	filter,
	map,
	retryWhen,
	scan,
	shareReplay,
	switchMap,
	take,
	tap,
} from 'rxjs/operators';
import { IOperativeUserRole, IResponseMessage, LocalStorageKeys, ServerMessages } from '../interfaces';
import { AllowedOrganizationsModel, Organization } from '../interfaces/accounts_list';
import { CacheService } from './cache.service';
import { OverridedMixpanel } from 'mixpanel-browser';
import { RestrictedSupportOrganizations } from '@app/support-request';
import { PartnerAliasEnum } from '@partners/partner-alias.enum';
import { User } from '@auth0/auth0-angular';
import { FeatureFlagManagerService } from '@app/infrastructure/feature-flag-manager/feature-flag-manager.service';
import { isEmptyObject } from '@utils/is-empty-helper';
import { CustomerType } from '@partners/customer-type.enum';
import { InitialResourcesResponse } from '@app/interfaces/initial-resources';
import { AppsVersion } from '@app/infrastructure/auth/app-configuration';

declare global {
	interface Window {
		zestyHost: {
			authToken: string;
			currentOrganization: Organization;
			sharedResponsePromisesByUrl: Record<string, Promise<unknown>>;
			featureFlags: Record<string, boolean>;
			isSupportedUser: boolean;
			permissions: string[];
			user: User;
			userRole: IOperativeUserRole;
			tempConfig: any;
			customerType: CustomerType;
			appsVersion?: AppsVersion;
		};
		mixpanel: OverridedMixpanel;
		HostRouter: {
			events: any;
		};
	}
}

@Injectable({
	providedIn: 'root',
})
// @Todo: Deprecated - instead start using class OrganizationService
export class OrganizationsService {
	private readonly ORGANIZATIONS_CACHE_KEY = 'organizations';
	private readonly RESOURCES_CACHE_KEY = 'resources';
	private readonly selectedOrganization = 'selected-organization';
	private readonly _isOrganizationOnlyCUR$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

	constructor(
		private http: HttpClient,
		private securityService: SecurityService,
		private cache: CacheService,
		private appConfig: ConfigFacadeService,
		private configFacadeService: ConfigFacadeService,
		private featureFlagManager: FeatureFlagManagerService,
	) {}

	public get isAzure(): boolean {
		const currentOrganization: Organization = this.cache.get(this.selectedOrganization);
		return currentOrganization.org_cloud_type === OrganizationTypes.Azure;
	}

	public get isCUROnly(): boolean {
		const currentOrganization: Organization = this.cache.get(this.selectedOrganization);
		return currentOrganization.cur_only;
	}

	public get isOrganizationOnlyCUR$(): Observable<boolean> {
		return this._isOrganizationOnlyCUR$.asObservable().pipe(distinctUntilChanged());
	}

	getInitialResources(): Observable<InitialResourcesResponse> {
		const cachedData$: Observable<InitialResourcesResponse> = this.cache.get$(this.RESOURCES_CACHE_KEY);
		if (cachedData$) {
			return cachedData$;
		}

		const url = `${this.appConfig.config.apiUrl}/bff/v1/initial-resources`;
		const data$ = this.http.get<InitialResourcesResponse>(url).pipe(
			retryWhen((errors) =>
				errors.pipe(
					scan((retryCount, error) => {
						if (retryCount >= 3) {
							throw error;
						}
						return retryCount + 1;
					}, 0),
					delay(1000), // 1 second delay between retries
				),
			),
			shareReplay(1),
		);
		this.cache.set$(this.RESOURCES_CACHE_KEY, data$);

		return data$;
	}

	updateOrganizations(data: AllowedOrganizationsModel): void {
		const cachedData$ = of(data).pipe(
			concatMap((data) => {
				const organizations = this.removeAzurePrefix(data.organizations);
				const currentOrg: Organization = getActiveOrganization(organizations);
				this.cache.set(this.selectedOrganization, currentOrg);
				window.zestyHost.currentOrganization = currentOrg;
				window.zestyHost.isSupportedUser = this.defineIsSupportAvailable(currentOrg);
				if (!currentOrg) {
					return this.switchOrganization(`${organizations[0].org_id}`).pipe(map((_) => organizations));
				}
				this.securityService.hasPotentialSavings.next(!currentOrg.is_managed);
				organizations.sort(this.sortByAlpha);
				organizations.sort(this.sortByManaged);
				return of(organizations);
			}),
			shareReplay(1),
		);
		this.cache.set$(this.ORGANIZATIONS_CACHE_KEY, cachedData$);
	}

	/**
	 * Fetch available organizations
	 * @param {boolean} allowCache - whether you take data from cache (true by default)
	 */
	getOrganizations(allowCache = true): Observable<Organization[]> {
		const cachedData$: Observable<Organization[]> = this.cache.get$(this.ORGANIZATIONS_CACHE_KEY);

		if (allowCache && cachedData$) {
			return cachedData$;
		}

		return this.fetchOrganizations();
	}

	getOU(org_id: number): Observable<IOUList> {
		const url = makeUri(this.appConfig.config.apiUrl, 'frontend/reseller', 'get-ou');
		return this.http.post<IOUList>(url, { org_id }).pipe(catchError((err) => handleError(err, null)));
	}

	/**
	 * Switch active organization.
	 */
	switchOrganization(org_id: string): Observable<IResponseMessage> {
		const url = makeUri(this.appConfig.config.apiUrl, 'frontend/organization', 'switch', org_id);
		return this.http.get<IResponseMessage>(url).pipe(
			tap((response) => {
				localStorage.removeItem(LocalStorageKeys.HAS_MANAGED);
				if (response.message === ServerMessages.success) {
					location.replace(`${location.origin}${AppRouteHelper.defaultRoute}`);
				}
			}),
		);
	}

	public getCurrentUserOrganization() {
		return this.getOrganizations().pipe(
			take(1),
			map((organizations) => organizations.filter((org) => org.is_selected)[0]),
			tap((res: IOrganization) => {
				if (res?.cur_only) {
					monthlyCostConfig.columnName = 'MTD Cost';
					monthlyCostConfig.valueSuffix = '/mtd';
				}
				this._isOrganizationOnlyCUR$.next(!!res?.cur_only);
			}),
			shareReplay(1),
		);
	}

	private fetchOrganizations(): Observable<Organization[]> {
		return this.featureFlagManager.flags$.pipe(
			filter((flags) => !isEmptyObject(flags)),
			take(1),
			switchMap(() => {
				const url = `${this.appConfig.config.apiUrl}/frontend/account/get-my-organizations-light`;
				const cachedData$ = this.http.get<AllowedOrganizationsModel>(url).pipe(
					concatMap((data) => {
						const organizations = this.removeAzurePrefix(data.organizations);
						const currentOrg: Organization = getActiveOrganization(organizations);
						this.cache.set(this.selectedOrganization, currentOrg);
						window.zestyHost.currentOrganization = currentOrg;
						window.zestyHost.isSupportedUser = this.defineIsSupportAvailable(currentOrg);
						if (!currentOrg) {
							return this.switchOrganization(`${organizations[0].org_id}`).pipe(map((_) => organizations));
						}
						this.securityService.hasPotentialSavings.next(!currentOrg.is_managed);
						organizations.sort(this.sortByAlpha);
						organizations.sort(this.sortByManaged);
						return of(organizations);
					}),
					shareReplay(1),
				);
				this.cache.set$(this.ORGANIZATIONS_CACHE_KEY, cachedData$);

				return cachedData$;
			}),
		);
	}

	private sortByAlpha(a: Organization, b: Organization): number {
		return a.org_name > b.org_name ? 1 : a.org_name < b.org_name ? -1 : 0;
	}

	private sortByManaged(a: Organization, b: Organization): number {
		return a.is_managed === b.is_managed ? 0 : a.is_managed ? -1 : 1;
	}

	private removeAzurePrefix(orgList: Organization[]): Organization[] {
		return orgList.map((orgItem) => {
			const nameWithoutAzurePrefix = orgItem.org_name?.replace(/^~Azure~ /, '') || 'unknown';

			return {
				...orgItem,
				org_name: nameWithoutAzurePrefix,
			};
		});
	}

	private defineIsSupportAvailable(organization: IOrganization): boolean {
		if (
			this.configFacadeService.partnerPrefix === PartnerAliasEnum.Zesty &&
			!(organization.org_id in RestrictedSupportOrganizations) &&
			!organization.reseller_managed
		) {
			return true;
		}
		return false;
	}
}
